import React, { useState, useRef, useEffect, useCallback } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { WorkItemStatus } from '../../types';

interface InlineEditStatusProps {
  value?: WorkItemStatus;
  onSave: (value?: WorkItemStatus) => void;
}

const InlineEditStatusComponent: React.FC<InlineEditStatusProps> = ({
  value: initialValue,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<WorkItemStatus | undefined>(initialValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const editStartTime = useRef<number>(0);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    if (value !== initialValue) {
      onSave(value);
    }
  }, [value, initialValue, onSave]);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Ignore clicks that happen within 300ms of opening
      if (Date.now() - editStartTime.current < 300) {
        return;
      }

      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        handleSave();
      }
    };

    // Add listener immediately but with timestamp check
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isEditing, handleSave]);

  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editStartTime.current = Date.now();
    setIsEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setValue(initialValue);
    }
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const getStatusStyle = () => {
    if (!value) return {};

    switch(value) {
      case 'In Progress':
        return styles.table.statusInProgress;
      case 'Done':
        return styles.table.statusDone;
      case 'Blocked':
        return styles.table.statusBlocked;
      default:
        return styles.table.statusBacklog;
    }
  };

  if (!isEditing) {
    return (
      <div
        style={(styles as any).inlineEdit?.statusContainer || {}}
        onDoubleClick={handleDoubleClick}
        title="Double-click to edit"
      >
        {value ? (
          <span style={{ ...styles.table.statusBadge, ...getStatusStyle() }}>
            {value}
          </span>
        ) : (
          <span style={(styles as any).inlineEdit?.placeholder || {}}>No status</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={(styles as any).inlineEdit?.editContainer || {}}>
      <select
        ref={selectRef}
        value={value || ''}
        onChange={(e) => setValue(e.target.value ? e.target.value as WorkItemStatus : undefined)}
        onKeyDown={handleKeyDown}
        style={(styles as any).inlineEdit?.statusSelect || {}}
      >
        <option value="">No Status</option>
        <option value="Backlog">Backlog</option>
        <option value="In Progress">In Progress</option>
        <option value="Done">Done</option>
        <option value="Blocked">Blocked</option>
      </select>
    </div>
  );
};

export const InlineEditStatus = React.memo(InlineEditStatusComponent);